// =============================================================================
// FILE: InvestorPublicPage.tsx
// PURPOSE: Premium Secure Investor Dashboard — Token-gated, executive-quality
//   Accessible via /#investor-TOKEN URL. No login required — token IS credential.
//   Includes: Hero, Company Intelligence, Roadmap, Metrics, Founder Letter,
//             Investment Opportunity, and Presentation Mode.
// =============================================================================

import { useState, useEffect, useRef } from 'react';

const API = (import.meta as any).env?.VITE_BACKEND_URL ?? '';

// ── Types ──────────────────────────────────────────────────────────────────────
interface TokenInfo {
  valid: boolean; name: string; company: string; email: string;
  permissions: Record<string, boolean>; expires_at: string; days_remaining: number;
}
interface DashboardData {
  investor_name: string; company: string; expires_at: string; permissions: Record<string, boolean>;
  capabilities?: any[]; platform_metrics?: Record<string, any>;
  roadmap?: any[]; financials?: any; investment?: any;
  founder_letter?: string; contact?: any; schedule_meeting_url?: string;
}
interface PublicMetrics {
  total_allocation_pct: number; available_pct: number; under_discussion_pct: number;
  current_tier_label: string; current_tier_status: string;
  invitations_sent: number; windows: any[];
}

// ── Palette ───────────────────────────────────────────────────────────────────
const BG     = '#02040a';
const PANEL  = 'rgba(255,255,255,0.03)';
const BORDER = 'rgba(255,255,255,0.08)';
const TEAL   = '#5eead4';
const AMBER  = '#f59e0b';
const GREEN  = '#4ade80';
const PURPLE = '#a78bfa';
const RED    = '#f87171';

const STATUS_CLR: Record<string, string> = { complete: GREEN, active: TEAL, planned: '#64748b', roadmap: PURPLE };
const IMPACT_CLR: Record<string, string> = { critical: RED, high: AMBER, medium: TEAL };

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(d: string | null): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return d; }
}

function SectionTitle({ label, title, color = TEAL }: { label: string; title: string; color?: string }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 9, color, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <h2 style={{ fontSize: 26, fontWeight: 800, color: 'rgba(241,245,249,0.95)', margin: 0, letterSpacing: '-0.5px' }}>{title}</h2>
    </div>
  );
}

function MetricTile({ label, value, color = TEAL, sub }: { label: string; value: string | number; color?: string; sub?: string }) {
  return (
    <div style={{ background: PANEL, border: `1px solid ${color}15`, borderRadius: 10, padding: '18px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 900, color, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.6)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
      {sub && <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.5)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Token Entry Form ──────────────────────────────────────────────────────────
function TokenEntry({ onVerify, onBack }: { onVerify: (token: string) => void; onBack?: () => void }) {
  const [input, setInput] = useState('');

  // ESC key exits
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && onBack) onBack(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onBack]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: BG, fontFamily: "'JetBrains Mono', monospace", padding: 24, position: 'relative' }}>

      {/* ── Back escape ──────────────────────────────────────────────────── */}
      {onBack && (
        <button
          data-testid="investor-portal-back-btn"
          onClick={onBack}
          style={{ position: 'absolute', top: 20, left: 20, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: 'rgba(148,163,184,0.7)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em', transition: 'all .15s' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#f1f5f9'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(148,163,184,0.7)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
        >
          ← Back to R2
        </button>
      )}
      {onBack && (
        <div style={{ position: 'absolute', top: 24, right: 20, fontSize: 10, color: 'rgba(100,116,139,0.35)', fontFamily: 'inherit' }}>
          Press Esc to exit
        </div>
      )}

      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: TEAL, letterSpacing: '-2px', marginBottom: 8 }}>R²</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'rgba(241,245,249,0.85)', marginBottom: 6 }}>Secure Investor Portal</div>
          <div style={{ fontSize: 11, color: 'rgba(100,116,139,0.6)' }}>Enter your investor access token to continue</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '28px 24px' }}>
          <label style={{ fontSize: 10, color: 'rgba(100,116,139,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 8 }}>Access Token</label>
          <input
            data-testid="investor-token-input"
            type="text" placeholder="XXXX-XXXX-XXXX" value={input}
            onChange={e => setInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && input.trim() && onVerify(input.trim())}
            style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px', color: 'rgba(241,245,249,0.9)', fontSize: 14, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', boxSizing: 'border-box', marginBottom: 14 }}
          />
          <button data-testid="investor-token-submit"
            onClick={() => input.trim() && onVerify(input.trim())}
            style={{ width: '100%', padding: '13px', background: `linear-gradient(135deg, ${TEAL}22 0%, ${TEAL}10 100%)`, border: `1px solid ${TEAL}55`, borderRadius: 8, color: TEAL, fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em', fontFamily: 'inherit', boxShadow: `0 0 16px ${TEAL}20, inset 0 1px 0 ${TEAL}15` }}>
            Verify Token
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 10, color: 'rgba(100,116,139,0.4)' }}>
          Tokens are time-limited and revocable. Contact john@rcd2engine.com for access.
        </div>
      </div>
    </div>
  );
}

// ── Presentation Mode Slide ───────────────────────────────────────────────────
const SLIDES = [
  { id: 'hero',       label: 'Vision' },
  { id: 'metrics',    label: 'Platform' },
  { id: 'engines',    label: 'Technology' },
  { id: 'roadmap',    label: 'Roadmap' },
  { id: 'letter',     label: "Founder's Letter" },
  { id: 'investment', label: 'Opportunity' },
];

// ── Main Dashboard ─────────────────────────────────────────────────────────────
function InvestorDashboard({ data, onLogin, presentMode, onTogglePresent, slideIdx, onSlide }:
  { data: DashboardData; onLogin?: () => void; presentMode: boolean; onTogglePresent: () => void; slideIdx: number; onSlide: (i: number) => void }
) {
  const metrics = data.platform_metrics || {};
  const roadmap = data.roadmap || [];
  const capabilities = data.capabilities || [];

  if (presentMode) {
    // ── Presentation Mode ─────────────────────────────────────────────────
    const slide = SLIDES[slideIdx];
    if (!slide) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: BG, display: 'flex', flexDirection: 'column', fontFamily: "'JetBrains Mono', monospace", zIndex: 9999 }}>
        {/* Slide nav */}
        <div style={{ padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {SLIDES.map((s, i) => (
              <button key={s.id} onClick={() => onSlide(i)}
                style={{ padding: '5px 12px', background: slideIdx === i ? `${TEAL}15` : 'rgba(255,255,255,0.03)', border: `1px solid ${slideIdx === i ? TEAL + '40' : BORDER}`, borderRadius: 5, fontSize: 9, color: slideIdx === i ? TEAL : 'rgba(100,116,139,0.6)', cursor: 'pointer', fontFamily: 'inherit' }}>
                {s.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: 'rgba(100,116,139,0.5)' }}>{slideIdx + 1} / {SLIDES.length}</span>
            <button onClick={onTogglePresent}
              style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 5, fontSize: 9, color: 'rgba(241,245,249,0.6)', cursor: 'pointer', fontFamily: 'inherit' }}>
              Exit Presentation
            </button>
          </div>
        </div>

        {/* Slide content */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 60px', overflowY: 'auto' }}>
          {slide.id === 'hero' && (
            <div style={{ textAlign: 'center', maxWidth: 700 }}>
              <div style={{ fontSize: 11, color: TEAL, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 24 }}>R2 Enterprise — Confidential Investor Briefing</div>
              <h1 style={{ fontSize: 52, fontWeight: 900, color: 'rgba(241,245,249,0.97)', letterSpacing: '-2px', lineHeight: 1.08, marginBottom: 20 }}>
                The Engineering<br /><span style={{ color: TEAL }}>Intelligence</span> Platform
              </h1>
              <p style={{ fontSize: 16, color: 'rgba(100,116,139,0.8)', lineHeight: 1.7, marginBottom: 32 }}>
                R2 eliminates the 60–80% of engineering project time spent on repetitive documentation, coordination, and compliance verification. We are building the Engineering Brain.
              </p>
              <div style={{ display: 'inline-flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                {['$42B TAM', 'Seed Stage', 'Enterprise Preview', '15 Active Engines'].map(t => (
                  <span key={t} style={{ padding: '6px 16px', background: `${TEAL}10`, border: `1px solid ${TEAL}25`, borderRadius: 20, fontSize: 10, color: TEAL, fontWeight: 700, letterSpacing: '0.08em' }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {slide.id === 'metrics' && (
            <div style={{ width: '100%', maxWidth: 800 }}>
              <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <div style={{ fontSize: 10, color: TEAL, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Platform Status</div>
                <h2 style={{ fontSize: 32, fontWeight: 800, color: 'rgba(241,245,249,0.95)', margin: 0 }}>R2 by the Numbers</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                {[
                  { label: 'Active Engines',      value: metrics.engines_active || 15,   color: TEAL  },
                  { label: 'Engineering Rules',    value: (metrics.engineering_rules || 14532).toLocaleString(), color: AMBER },
                  { label: 'Building Types',       value: metrics.building_types || 128,  color: GREEN },
                  { label: 'AI Models',            value: metrics.ai_models || 9,         color: PURPLE},
                  { label: 'Disciplines',          value: metrics.disciplines_covered || 11, color: TEAL },
                  { label: 'LOD Levels',           value: metrics.lod_levels || 5,        color: AMBER },
                  { label: 'Export Formats',       value: metrics.export_formats || 6,    color: GREEN },
                  { label: 'Platform Health',      value: metrics.platform_health || 'Operational', color: PURPLE },
                ].map(m => (
                  <MetricTile key={m.label} label={m.label} value={m.value} color={m.color} />
                ))}
              </div>
            </div>
          )}

          {slide.id === 'engines' && (
            <div style={{ width: '100%', maxWidth: 900 }}>
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <div style={{ fontSize: 10, color: TEAL, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Technology</div>
                <h2 style={{ fontSize: 32, fontWeight: 800, color: 'rgba(241,245,249,0.95)', margin: 0 }}>Six Intelligence Engines</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {(capabilities.length > 0 ? capabilities : [
                  { category: 'Building Intelligence',   features: ['AI-powered BIM generation', 'Airport / Hospital / Data Centre worlds', 'LOD 100–500 progression'] },
                  { category: 'Adaptive Engineering',    features: ['17 dependent systems per wall change', 'Parametric design sliders', 'Platform-agnostic adapters'] },
                  { category: 'R2DL Design Language',    features: ['Natural language → R2DL translation', 'BUILD, CHANGE, OPTIMIZE, EXPORT', 'AI validation engine'] },
                  { category: 'Disaster Resilience',     features: ['Seismic / Flood / Fire / Power modules', 'T+0 to T+7 day timelines', 'Recovery plan generation'] },
                  { category: 'Deliverables',            features: ['Revit, AutoCAD, IFC, PDF', 'Excel schedules', 'Engineering specifications'] },
                  { category: 'Security Architecture',   features: ['Engineering intelligence server-side', 'No source code in outputs', 'Role-based access control'] },
                ]).map((cap: any) => (
                  <div key={cap.category} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '18px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, marginBottom: 10 }}>{cap.category}</div>
                    {(cap.features || []).map((f: string) => (
                      <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                        <span style={{ color: TEAL, fontSize: 8, marginTop: 3, flexShrink: 0 }}>◆</span>
                        <span style={{ fontSize: 10, color: 'rgba(100,116,139,0.75)', lineHeight: 1.5 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {slide.id === 'roadmap' && (
            <div style={{ width: '100%', maxWidth: 900 }}>
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <div style={{ fontSize: 10, color: TEAL, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Roadmap</div>
                <h2 style={{ fontSize: 32, fontWeight: 800, color: 'rgba(241,245,249,0.95)', margin: 0 }}>Building the Engineering Intelligence Standard</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
                {(roadmap.length > 0 ? roadmap : [
                  { phase: 'Phase 1', title: 'Foundation',          status: 'complete',  quarter: 'Q1 2026', items: ['R2DL', 'BWSE Airport', 'RAE Engine', 'Document Intelligence'] },
                  { phase: 'Phase 2', title: 'Resilience',          status: 'active',    quarter: 'Q2 2026', items: ['DCRE Engine', 'Investor Portal', '3D Visualization', 'Seismic Analysis'] },
                  { phase: 'Phase 3', title: 'Multi-Discipline',     status: 'planned',   quarter: 'Q3 2026', items: ['Hospital World', 'Data Centre World', 'Campus World'] },
                  { phase: 'Phase 4', title: 'Enterprise Scale',     status: 'planned',   quarter: 'Q4 2026', items: ['Enterprise SSO', 'API Access', 'White-Label'] },
                  { phase: 'Phase 5', title: 'Global Platform',      status: 'roadmap',   quarter: 'Q1 2027', items: ['IFC/OpenBIM', 'R2 Marketplace', 'Partner SDK'] },
                ]).map((r: any) => (
                  <div key={r.phase} style={{ background: PANEL, border: `1px solid ${STATUS_CLR[r.status] || BORDER}20`, borderRadius: 10, padding: '16px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_CLR[r.status] || '#64748b', boxShadow: r.status === 'active' ? `0 0 6px ${TEAL}` : 'none' }} />
                      <span style={{ fontSize: 8, color: STATUS_CLR[r.status] || '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{r.status}</span>
                    </div>
                    <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.5)', marginBottom: 4 }}>{r.phase} · {r.quarter}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(241,245,249,0.8)', marginBottom: 10 }}>{r.title}</div>
                    {(r.items || []).map((item: string) => (
                      <div key={item} style={{ display: 'flex', gap: 5, marginBottom: 4 }}>
                        <span style={{ color: STATUS_CLR[r.status] || '#64748b', fontSize: 7, marginTop: 3 }}>◆</span>
                        <span style={{ fontSize: 9, color: 'rgba(100,116,139,0.6)' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {slide.id === 'letter' && data.founder_letter && (
            <div style={{ maxWidth: 640, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: TEAL, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>From the Founder</div>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: 'rgba(241,245,249,0.95)', marginBottom: 32 }}>A Letter from John Bismonte, RCDD</h2>
              <div style={{ fontSize: 13, color: 'rgba(100,116,139,0.75)', lineHeight: 1.9, textAlign: 'left', whiteSpace: 'pre-wrap' }}>{data.founder_letter}</div>
              {data.contact && (
                <div style={{ marginTop: 32, display: 'flex', gap: 12, justifyContent: 'center' }}>
                  <a href={`mailto:${data.contact.email}`} style={{ padding: '10px 24px', background: `${TEAL}15`, border: `1px solid ${TEAL}40`, borderRadius: 7, color: TEAL, fontSize: 11, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>{data.contact.email}</a>
                </div>
              )}
            </div>
          )}

          {slide.id === 'investment' && (
            <div style={{ maxWidth: 700, width: '100%' }}>
              <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <div style={{ fontSize: 10, color: PURPLE, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Investment Opportunity</div>
                <h2 style={{ fontSize: 32, fontWeight: 800, color: 'rgba(241,245,249,0.95)', margin: 0 }}>Join the R2 Mission</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                {[
                  { label: 'Stage',           value: 'Seed', color: TEAL },
                  { label: 'Market (TAM)',     value: '$42B', color: GREEN },
                  { label: 'ARR Target Y1',    value: '$1.2M', color: AMBER },
                  { label: 'ARR Target Y3',    value: '$18M', color: RED },
                ].map(m => <MetricTile key={m.label} label={m.label} value={m.value} color={m.color} />)}
              </div>
              <div style={{ background: PANEL, border: `1px solid ${PURPLE}20`, borderRadius: 10, padding: '20px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(241,245,249,0.85)', marginBottom: 12 }}>Use of Funds</div>
                {['Engineering team expansion', 'Sales & marketing', 'Platform infrastructure', 'R&D for Hospital & Data Centre worlds'].map(item => (
                  <div key={item} style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
                    <span style={{ color: PURPLE, fontSize: 8, marginTop: 4, flexShrink: 0 }}>◆</span>
                    <span style={{ fontSize: 11, color: 'rgba(100,116,139,0.75)' }}>{item}</span>
                  </div>
                ))}
              </div>
              {data.contact && (
                <div style={{ marginTop: 24, textAlign: 'center' }}>
                  <a href={`mailto:${data.contact.email}`}
                    style={{ display: 'inline-block', padding: '12px 32px', background: PURPLE, borderRadius: 8, color: BG, fontSize: 13, fontWeight: 800, cursor: 'pointer', textDecoration: 'none', letterSpacing: '0.04em' }}>
                    Contact Founder
                  </a>
                  {data.schedule_meeting_url && (
                    <a href={data.schedule_meeting_url} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-block', marginLeft: 10, padding: '12px 32px', background: `${PURPLE}15`, border: `1px solid ${PURPLE}40`, borderRadius: 8, color: PURPLE, fontSize: 13, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>
                      Schedule Meeting
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Slide navigation arrows */}
        <div style={{ padding: '16px 32px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={() => onSlide(Math.max(0, slideIdx - 1))} disabled={slideIdx === 0}
            style={{ padding: '8px 20px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 6, color: slideIdx === 0 ? 'rgba(100,116,139,0.3)' : 'rgba(241,245,249,0.7)', cursor: slideIdx === 0 ? 'default' : 'pointer', fontSize: 10, fontFamily: 'inherit' }}>
            ← Previous
          </button>
          <button onClick={() => onSlide(Math.min(SLIDES.length - 1, slideIdx + 1))} disabled={slideIdx === SLIDES.length - 1}
            style={{ padding: '8px 20px', background: slideIdx < SLIDES.length - 1 ? `${TEAL}15` : 'rgba(255,255,255,0.04)', border: `1px solid ${slideIdx < SLIDES.length - 1 ? TEAL + '40' : BORDER}`, borderRadius: 6, color: slideIdx < SLIDES.length - 1 ? TEAL : 'rgba(100,116,139,0.3)', cursor: slideIdx < SLIDES.length - 1 ? 'pointer' : 'default', fontSize: 10, fontFamily: 'inherit' }}>
            Next →
          </button>
        </div>
      </div>
    );
  }

  // ── Full Dashboard View ─────────────────────────────────────────────────────
  return (
    <div data-testid="investor-dashboard" style={{ minHeight: '100vh', background: BG, fontFamily: "'JetBrains Mono', monospace" }}>

      {/* Sticky header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(2,4,10,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${BORDER}`, padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22, fontWeight: 900, color: TEAL, letterSpacing: '-1px' }}>R²</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(241,245,249,0.7)' }}>Investor Portal</span>
          <span style={{ padding: '2px 8px', background: `${TEAL}10`, border: `1px solid ${TEAL}20`, borderRadius: 3, fontSize: 8, color: TEAL, letterSpacing: '0.1em' }}>SECURE · READ-ONLY</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: 'rgba(100,116,139,0.5)' }}>Expires: {fmt(data.expires_at)}</span>
          <button data-testid="presentation-mode-btn" onClick={onTogglePresent}
            style={{ padding: '7px 16px', background: `${TEAL}12`, border: `1px solid ${TEAL}35`, borderRadius: 6, color: TEAL, fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', fontFamily: 'inherit' }}>
            Start Presentation
          </button>
          {onLogin && (
            <button onClick={onLogin}
              style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 6, color: 'rgba(241,245,249,0.5)', fontSize: 9, cursor: 'pointer', fontFamily: 'inherit' }}>
              Exit
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1060, margin: '0 auto', padding: '48px 28px' }}>

        {/* Welcome */}
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 10, color: TEAL, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 10 }}>Confidential — {data.investor_name || 'Investor'} · {data.company}</div>
          <h1 style={{ fontSize: 36, fontWeight: 900, color: 'rgba(241,245,249,0.97)', letterSpacing: '-1px', marginBottom: 14 }}>
            The Engineering<br /><span style={{ color: TEAL }}>Intelligence Platform</span>
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(100,116,139,0.7)', maxWidth: 580, lineHeight: 1.7 }}>
            R2 is building the engineering brain — a platform where intelligence lives in the cloud, and every CAD/BIM application becomes an interchangeable output device.
          </p>
        </div>

        {/* Platform Metrics */}
        {data.platform_metrics && (
          <div style={{ marginBottom: 56 }}>
            <SectionTitle label="Platform Status" title="R2 by the Numbers" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <MetricTile label="Active Engines"       value={metrics.engines_active || 15}                        color={TEAL}   />
              <MetricTile label="Engineering Rules"    value={(metrics.engineering_rules || 14532).toLocaleString()} color={AMBER}  />
              <MetricTile label="Building Types"       value={metrics.building_types || 128}                       color={GREEN}  />
              <MetricTile label="AI Models"            value={metrics.ai_models || 9}                              color={PURPLE} />
              <MetricTile label="Disciplines"          value={metrics.disciplines_covered || 11}                   color={TEAL}   />
              <MetricTile label="LOD Levels"           value={metrics.lod_levels || 5}                             color={AMBER}  />
              <MetricTile label="Export Formats"       value={metrics.export_formats || 6}                         color={GREEN}  />
              <MetricTile label="Platform Version"     value={`v${metrics.current_version || '1.0'}`}              color={PURPLE} sub={`Build ${metrics.build || '2026.07'}`} />
            </div>
          </div>
        )}

        {/* Capabilities */}
        {data.capabilities && (
          <div style={{ marginBottom: 56 }}>
            <SectionTitle label="Technology" title="Six Intelligence Engines" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {data.capabilities.map((cap: any) => (
                <div key={cap.category} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '20px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEAL, marginBottom: 12 }}>{cap.category}</div>
                  {(cap.features || []).map((f: string) => (
                    <div key={f} style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
                      <span style={{ color: TEAL, fontSize: 8, marginTop: 3, flexShrink: 0 }}>◆</span>
                      <span style={{ fontSize: 10, color: 'rgba(100,116,139,0.7)', lineHeight: 1.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Roadmap */}
        {data.roadmap && (
          <div style={{ marginBottom: 56 }}>
            <SectionTitle label="Development Roadmap" title="Building the Engineering Intelligence Standard" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
              {data.roadmap.map((r: any) => (
                <div key={r.phase} style={{ background: PANEL, border: `1px solid ${STATUS_CLR[r.status] || BORDER}20`, borderRadius: 10, padding: '16px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_CLR[r.status] || '#64748b', boxShadow: r.status === 'active' ? `0 0 6px ${TEAL}` : 'none' }} />
                    <span style={{ fontSize: 8, color: STATUS_CLR[r.status] || '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{r.status}</span>
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.5)', marginBottom: 3 }}>{r.phase} · {r.quarter}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(241,245,249,0.8)', marginBottom: 10 }}>{r.title}</div>
                  {(r.items || []).map((item: string) => (
                    <div key={item} style={{ display: 'flex', gap: 5, marginBottom: 4 }}>
                      <span style={{ color: STATUS_CLR[r.status] || '#64748b', fontSize: 7, marginTop: 3 }}>◆</span>
                      <span style={{ fontSize: 9, color: 'rgba(100,116,139,0.6)' }}>{item}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Founder Letter */}
        {data.founder_letter && (
          <div style={{ marginBottom: 56 }}>
            <SectionTitle label="From the Founder" title="A Personal Message" color={AMBER} />
            <div style={{ background: `${AMBER}04`, border: `1px solid ${AMBER}15`, borderRadius: 12, padding: '32px 36px' }}>
              <div style={{ fontSize: 12, color: 'rgba(100,116,139,0.8)', lineHeight: 1.9, whiteSpace: 'pre-wrap', maxWidth: 680 }}>{data.founder_letter}</div>
              {data.contact && (
                <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${AMBER}15`, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <a href={`mailto:${data.contact.email}`}
                    style={{ padding: '9px 20px', background: `${AMBER}15`, border: `1px solid ${AMBER}35`, borderRadius: 7, color: AMBER, fontSize: 10, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}>
                    {data.contact.email}
                  </a>
                  {data.contact.linkedin && (
                    <a href={data.contact.linkedin} target="_blank" rel="noreferrer"
                      style={{ padding: '9px 20px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 7, color: 'rgba(241,245,249,0.6)', fontSize: 10, textDecoration: 'none' }}>
                      LinkedIn
                    </a>
                  )}
                  {data.schedule_meeting_url && (
                    <a href={data.schedule_meeting_url} target="_blank" rel="noreferrer"
                      style={{ padding: '9px 20px', background: `${TEAL}15`, border: `1px solid ${TEAL}35`, borderRadius: 7, color: TEAL, fontSize: 10, fontWeight: 700, textDecoration: 'none' }}>
                      Schedule Meeting
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Investment section */}
        <div style={{ marginBottom: 40 }}>
          <SectionTitle label="Investment Opportunity" title="Join the R2 Mission" color={PURPLE} />
          <div style={{ background: `${PURPLE}06`, border: `1px solid ${PURPLE}20`, borderRadius: 12, padding: '32px 36px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
              {[
                { label: 'Stage',           value: data.investment?.funding_stage || 'Seed', color: TEAL },
                { label: 'Market (TAM)',     value: data.financials?.market_size_tam || '$42B BIM Market', color: GREEN },
                { label: 'ARR Target Y1',    value: data.financials?.arr_target_y1 || '$1.2M', color: AMBER },
                { label: 'ARR Target Y3',    value: data.financials?.arr_target_y3 || '$18M', color: RED },
              ].map(m => <MetricTile key={m.label} label={m.label} value={m.value} color={m.color} />)}
            </div>
            <p style={{ fontSize: 12, color: 'rgba(100,116,139,0.7)', marginBottom: 0 }}>
              {data.investment?.note || 'Investment package details and full financial projections are available under NDA. Contact the founder directly to schedule a confidential discussion.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.4)' }}>R2 Enterprise · Confidential Investor Materials · Not for Distribution</div>
          <div style={{ fontSize: 9, color: 'rgba(100,116,139,0.4)' }}>Session expires: {fmt(data.expires_at)}</div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
//  MAIN EXPORT
// =============================================================================

export function InvestorPublicPage({ token, onLogin }: { token?: string | null; onLogin?: () => void }) {
  const [state, setState] = useState<'loading' | 'token_entry' | 'verifying' | 'dashboard' | 'error'>('loading');
  const [tokenValue, setTokenValue] = useState(token || '');
  const [dashData, setDashData]     = useState<DashboardData | null>(null);
  const [errorMsg, setErrorMsg]     = useState('');
  const [presentMode, setPresentMode] = useState(false);
  const [slideIdx, setSlideIdx]     = useState(0);

  // Also fall back to old public metrics endpoint if no token
  const [pubMetrics, setPubMetrics] = useState<PublicMetrics | null>(null);

  useEffect(() => {
    if (token) {
      verifyAndLoad(token);
    } else {
      // No token — try old public endpoint, then show token entry
      fetchPublicMetrics();
      setState('token_entry');
    }
  }, [token]);

  const fetchPublicMetrics = async () => {
    try {
      const r = await fetch(`${API}/api/investor/public`);
      if (r.ok) setPubMetrics(await r.json());
    } catch {}
  };

  const verifyAndLoad = async (t: string) => {
    setState('verifying');
    try {
      const vr = await fetch(`${API}/api/investor/verify/${t}`);
      if (!vr.ok) {
        const err = await vr.json().catch(() => ({ detail: 'Invalid token' }));
        setErrorMsg(err.detail || 'Access denied');
        setState('error');
        return;
      }
      const dashR = await fetch(`${API}/api/investor/dashboard/${t}`);
      if (!dashR.ok) throw new Error('Dashboard load failed');
      setDashData(await dashR.json());
      setState('dashboard');
    } catch (e: any) {
      setErrorMsg(e.message || 'Failed to load investor dashboard');
      setState('error');
    }
  };

  if (state === 'loading' || state === 'verifying') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: BG, fontFamily: "'JetBrains Mono', monospace" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: TEAL, marginBottom: 16 }}>R²</div>
          <div style={{ fontSize: 11, color: 'rgba(100,116,139,0.6)' }}>{state === 'verifying' ? 'Verifying access token…' : 'Loading…'}</div>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: BG, fontFamily: "'JetBrains Mono', monospace", padding: 24, position: 'relative' }}>
        {onLogin && (
          <button data-testid="investor-error-back-btn" onClick={onLogin}
            style={{ position: 'absolute', top: 20, left: 20, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: 'rgba(148,163,184,0.7)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            ← Back to R2
          </button>
        )}
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: TEAL, marginBottom: 24 }}>R²</div>
          <div style={{ background: `${RED}08`, border: `1px solid ${RED}30`, borderRadius: 10, padding: '24px', marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: RED, marginBottom: 8 }}>Access Denied</div>
            <div style={{ fontSize: 11, color: 'rgba(100,116,139,0.7)' }}>{errorMsg}</div>
          </div>
          <button onClick={() => { setErrorMsg(''); setState('token_entry'); }}
            style={{ padding: '10px 24px', background: `${TEAL}15`, border: `1px solid ${TEAL}35`, borderRadius: 7, color: TEAL, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Try a Different Token
          </button>
        </div>
      </div>
    );
  }

  if (state === 'token_entry') {
    return <TokenEntry onVerify={(t) => { setTokenValue(t); verifyAndLoad(t); }} onBack={onLogin} />;
  }

  if (state === 'dashboard' && dashData) {
    return (
      <InvestorDashboard
        data={dashData}
        onLogin={onLogin}
        presentMode={presentMode}
        onTogglePresent={() => setPresentMode(v => !v)}
        slideIdx={slideIdx}
        onSlide={setSlideIdx}
      />
    );
  }

  return null;
}
